import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Snackbar,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useMetadata } from '../../../contexts/MetadataContext';
import { Lookup } from '../../../system/lookup';
import LocaleEditor from './LocaleEditor';
import { snakeCase, toUpper } from 'lodash';
import {
  addIdsToTemplate,
  FieldWithId,
  GroupingWithId,
  GroupWithId,
  PropertyTemplatesWithIds,
  SectionWithId,
  TemplateWithIds,
} from './MetadataInterfaces';
import EditModeToggle, { EditMode } from './EditModeToggle';
import TemplateFieldEditor from './TemplateFieldEditor';
import TemplateGroupingsEditor from './TemplateGroupingsEditor';
import { useService } from '../../../contexts/ServiceContext';
import { inc } from 'semver';
import SelectionModeToggle, { SelectionType } from './SelectionModeToggle';
import { useHotkeys } from 'react-hotkeys-hook';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import RoomFeaturesEditor from './RoomFeaturesEditor';
import { useTenant } from '../../../contexts/TenantContext';
import HelpTextEditor from './HelpTextEditor';

const translationFields = [
  'Property',
  'OpenHouse',
  'Media',
  'Unit',
  'Room',
  'GreenVerification',
];

const lookupsFields = ['Room Features'];

type ClipboardItem = {
  id: string;
  sourcePropertyType: string;
};

type Clipboard = {
  type: SelectionType;
  items: ClipboardItem[];
} | null;

const TemplateEditor: React.FC = () => {
  const { lookup, propertyTemplates, fields } = useMetadata();
  const { tenant } = useTenant();
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupWithId | null>(null);
  const [selectedSection, setSelectedSection] = useState<SectionWithId | null>(
    null,
  );
  const [showToast, setShowToast] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(true);
  const [translationOpen, setTranslationOpen] = useState(false);
  const [lookupsOpen, setLookupsOpen] = useState(false);
  const [helpTextOpen, setHelpTextOpen] = useState(false);
  const [helpText, setHelpText] = useState<Record<string, string>>({});
  const [localTemplate, setLocalTemplate] = useState<PropertyTemplatesWithIds>(
    () => addIdsToTemplate(propertyTemplates),
  );
  const [editMode, setEditMode] = useState<EditMode>('fields');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectionType, setSelectionType] = useState<SelectionType>('none');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const { currentMetadata } = useMetadata();
  const [currentVersion, setCurrentVersion] = useState('');
  const [copyMode, setCopyMode] = useState<'copy' | 'cut' | null>(null);
  const [clipboard, setClipboard] = useState<Clipboard>(null);

  const { post } = useService();

  const toDisplayValue = (item: string) => {
    const matchingItem = propertyOptions.find(
      (option) => item === option.LookupValue,
    );
    if (!matchingItem) {
      return item;
    }

    return matchingItem.DisplayValue || matchingItem.LookupValue;
  };

  const propertyOptions = lookup(Lookup.PROPERTY_TYPE) || [];
  const templateKey = toUpper(
    snakeCase(selectedItem ? toDisplayValue(selectedItem) : ''),
  );

  // Compute unique resource types from fields for Help Text section
  const helpTextResourceTypes = useMemo(() => {
    const allFields = fields();
    if (!allFields) return [];
    const resourceTypes = new Set(allFields.map((f) => f.ResourceType));
    return Array.from(resourceTypes).filter(Boolean).sort();
  }, [fields]);

  // Check if selectedItem is a help text resource type
  const isHelpTextResource = selectedItem?.startsWith('HelpText:');
  const selectedHelpTextResource = isHelpTextResource
    ? selectedItem?.replace('HelpText:', '')
    : null;

  useEffect(() => {
    const metadata = currentMetadata();
    if (metadata) {
      setCurrentVersion(metadata.version);
      // Load existing help text if available
      if ((metadata as any).help) {
        setHelpText((metadata as any).help);
      }
    }
  }, [currentMetadata]);

  const handleItemClick = (item: string) => {
    setSelectedItem(item);
    setSelectedGroup(null);
    setSelectedSection(null);
  };

  const handleGroupClick = (group: GroupWithId) => {
    setSelectedGroup(group);
    setSelectedSection(null);
  };

  const handleSectionClick = (section: SectionWithId) => {
    setSelectedSection(section);
  };

  const handleSelectionModeToggle = (isEnabled: boolean) => {
    setIsSelectionMode(isEnabled);
    if (!isEnabled) {
      setSelectionType('none');
      setSelectedItems([]);
      setCopyMode(null);
      setClipboard(null);
    }
  };

  const handleSelectionChange = (type: SelectionType, id: string) => {
    if (selectionType !== type && selectionType !== 'none') {
      // If changing selection type, clear previous selections
      setSelectionType(type);
      setSelectedItems([id]);
    } else {
      const newSelectedItems = selectedItems.includes(id)
        ? selectedItems.filter((item) => item !== id)
        : [...selectedItems, id];
      setSelectionType(type);
      setSelectedItems(newSelectedItems);
    }
  };

  const handleCopy = useCallback(() => {
    if (isSelectionMode && selectedItems.length > 0 && selectedItem) {
      setCopyMode('copy');
      setClipboard({
        type: selectionType,
        items: selectedItems.map((id) => ({
          id,
          sourcePropertyType: templateKey,
        })),
      });
    }
  }, [
    isSelectionMode,
    selectedItems,
    selectionType,
    selectedItem,
    templateKey,
  ]);

  const handleCut = useCallback(() => {
    if (isSelectionMode && selectedItems.length > 0 && selectedItem) {
      setCopyMode('cut');
      setClipboard({
        type: selectionType,
        items: selectedItems.map((id) => ({
          id,
          sourcePropertyType: templateKey,
        })),
      });
    }
  }, [
    isSelectionMode,
    selectedItems,
    selectionType,
    selectedItem,
    templateKey,
  ]);

  useHotkeys('ctrl+c', handleCopy, [handleCopy]);
  useHotkeys('ctrl+x', handleCut, [handleCut]);

  const updateTemplate = useCallback(
    (
      propertyType: keyof PropertyTemplatesWithIds,
      updatedTemplate: TemplateWithIds,
    ) => {
      setLocalTemplate((prevTemplate) => ({
        ...prevTemplate,
        [propertyType]: updatedTemplate,
      }));
    },
    [],
  );

  const updateGroups = useCallback(
    (updatedGroups: GroupWithId[]) => {
      if (selectedItem) {
        updateTemplate(templateKey as keyof PropertyTemplatesWithIds, {
          ...localTemplate[templateKey as keyof PropertyTemplatesWithIds],
          groups: updatedGroups,
        });
      }
    },
    [selectedItem, templateKey, localTemplate, updateTemplate],
  );

  const updateSections = useCallback(
    (updatedSections: SectionWithId[]) => {
      if (selectedItem && selectedGroup) {
        const updatedGroups = localTemplate[
          templateKey as keyof PropertyTemplatesWithIds
        ].groups.map((g) =>
          g.id === selectedGroup.id ? { ...g, sections: updatedSections } : g,
        );
        updateTemplate(templateKey as keyof PropertyTemplatesWithIds, {
          ...localTemplate[templateKey as keyof PropertyTemplatesWithIds],
          groups: updatedGroups,
        });
        setSelectedGroup({ ...selectedGroup, sections: updatedSections });
      }
    },
    [selectedItem, selectedGroup, templateKey, localTemplate, updateTemplate],
  );

  const updateFields = useCallback(
    (updatedFields: FieldWithId[]) => {
      if (selectedItem && selectedGroup && selectedSection) {
        const updatedSections = selectedGroup.sections.map((s) =>
          s.id === selectedSection.id ? { ...s, fields: updatedFields } : s,
        );

        const updatedGroups = localTemplate[
          templateKey as keyof PropertyTemplatesWithIds
        ].groups.map((g) =>
          g.id === selectedGroup.id ? { ...g, sections: updatedSections } : g,
        );

        const updatedTemplate = {
          ...localTemplate[templateKey as keyof PropertyTemplatesWithIds],
          groups: updatedGroups,
        };

        updateTemplate(
          templateKey as keyof PropertyTemplatesWithIds,
          updatedTemplate,
        );
        setSelectedGroup({ ...selectedGroup, sections: updatedSections });
        setSelectedSection({ ...selectedSection, fields: updatedFields });
      }
    },
    [
      selectedItem,
      selectedGroup,
      selectedSection,
      templateKey,
      localTemplate,
      updateTemplate,
    ],
  );

  const updateGroupings = useCallback(
    (updatedGroupings: GroupingWithId[]) => {
      if (selectedItem) {
        updateTemplate(templateKey as keyof PropertyTemplatesWithIds, {
          ...localTemplate[templateKey as keyof PropertyTemplatesWithIds],
          groupings: updatedGroupings,
        });
      }
    },
    [selectedItem, templateKey, localTemplate, updateTemplate],
  );

  const handleSaveTemplate = useCallback(async () => {
    const metadata = currentMetadata();
    if (!metadata) {
      setToastMessage('No metadata available');
      setShowToast(true);
      return;
    }

    setIsSaving(true);
    setToastMessage('Saving Changes...');
    setShowToast(true);

    try {
      const newVersion = inc(currentVersion, 'patch');
      const newTemplate: any = Object.entries(localTemplate).reduce(
        (acc, [key, value]) => {
          acc[key] = {
            compareForAgents: value.compareForAgents,
            compareForListings: value.compareForListings,
            groupings: value.groupings.map((grouping) => ({
              ...grouping,
              id: undefined,
            })),
            groups: value.groups.map((group) => ({
              ...group,
              id: undefined,
              sections: group.sections.map((section) => ({
                ...section,
                id: undefined,
                fields: section.fields.map((field) => ({
                  ...field,
                  id: undefined,
                })),
              })),
            })),
            modifications: {
              removeFromLookups:
                value.modifications?.removeFromLookups?.map((entry) => ({
                  ...entry,
                })) || [],
            },
          };
          return acc;
        },
        {} as any,
      );

      // Only include help text entries that have content
      const filteredHelpText = Object.entries(helpText).reduce(
        (acc, [key, value]) => {
          if (value && value.trim()) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      const request = {
        name: tenant,
        version: newVersion,
        template: newTemplate,
        translation: metadata.translation,
        ...(Object.keys(filteredHelpText).length > 0 && {
          help: filteredHelpText,
        }),
      };

      console.log('Saving template:', request);
      await post('save-template', {}, request);

      // Update the local version after successful save
      setCurrentVersion(`${newVersion}`);

      setToastMessage('Changes Saved');
      setShowToast(true);
    } catch (error) {
      console.error('Error saving template:', error);
      setToastMessage('Error saving changes');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  }, [currentMetadata, post, localTemplate, currentVersion, helpText, tenant]);

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const handleTemplatesToggle = () => {
    setTemplatesOpen(!templatesOpen);
  };

  const handleTranslationToggle = () => {
    setTranslationOpen(!translationOpen);
  };

  const handleLookupsToggle = () => {
    setLookupsOpen(!lookupsOpen);
  };

  const handleHelpTextToggle = () => {
    setHelpTextOpen(!helpTextOpen);
  };

  const handlePaste = useCallback(() => {
    if (!clipboard || !copyMode || !selectedItem) {
      console.log(
        'Cannot paste: no clipboard data, copy mode, or selected property type',
      );
      return;
    }

    const destinationPropertyType = templateKey;

    // Create a deep copy of the local template to work with
    const updatedTemplates = JSON.parse(JSON.stringify(localTemplate));

    clipboard.items.forEach(({ id: itemId, sourcePropertyType }) => {
      switch (clipboard.type) {
        case 'groupings': {
          const grouping = updatedTemplates[sourcePropertyType].groupings.find(
            (g: GroupingWithId) => g.id === itemId,
          );
          if (grouping) {
            if (copyMode === 'cut') {
              updatedTemplates[sourcePropertyType].groupings = updatedTemplates[
                sourcePropertyType
              ].groupings.filter((g: GroupingWithId) => g.id !== itemId);
            }
            const groupingToAdd =
              copyMode === 'copy'
                ? { ...grouping, id: crypto.randomUUID() }
                : grouping;
            updatedTemplates[destinationPropertyType].groupings.push(
              groupingToAdd,
            );
          }
          break;
        }
        case 'groups': {
          const group = updatedTemplates[sourcePropertyType].groups.find(
            (g: GroupWithId) => g.id === itemId,
          );
          if (group) {
            if (copyMode === 'cut') {
              updatedTemplates[sourcePropertyType].groups = updatedTemplates[
                sourcePropertyType
              ].groups.filter((g: GroupWithId) => g.id !== itemId);
            }
            const groupToAdd =
              copyMode === 'copy'
                ? {
                    ...group,
                    id: crypto.randomUUID(),
                    sections: group.sections.map((s: SectionWithId) => ({
                      ...s,
                      id: crypto.randomUUID(),
                      fields: s.fields.map((f: FieldWithId) => ({
                        ...f,
                        id: crypto.randomUUID(),
                      })),
                    })),
                  }
                : group;
            updatedTemplates[destinationPropertyType].groups.push(groupToAdd);
          }
          break;
        }

        case 'sections': {
          if (!selectedGroup) {
            console.log('Cannot paste sections: no group selected');
            return;
          }
          const sourceGroup = updatedTemplates[sourcePropertyType].groups.find(
            (g: GroupWithId) =>
              g.sections.some((s: SectionWithId) => s.id === itemId),
          );
          const section = sourceGroup?.sections.find(
            (s: SectionWithId) => s.id === itemId,
          );
          if (section) {
            if (copyMode === 'cut') {
              sourceGroup.sections = sourceGroup.sections.filter(
                (s: SectionWithId) => s.id !== itemId,
              );
            }
            const sectionToAdd =
              copyMode === 'copy'
                ? {
                    ...section,
                    id: crypto.randomUUID(),
                    fields: section.fields.map((f: FieldWithId) => ({
                      ...f,
                      id: crypto.randomUUID(),
                    })),
                  }
                : section;
            const destGroup = updatedTemplates[
              destinationPropertyType
            ].groups.find((g: GroupWithId) => g.id === selectedGroup.id);
            if (destGroup) {
              destGroup.sections.push(sectionToAdd);
            }
          }
          break;
        }
        case 'fields': {
          if (!selectedSection) {
            console.log('Cannot paste fields: no section selected');
            return;
          }
          const sourceGroupForField = updatedTemplates[
            sourcePropertyType
          ].groups.find((g: GroupWithId) =>
            g.sections.some((s: SectionWithId) =>
              s.fields.some((f: FieldWithId) => f.id === itemId),
            ),
          );
          const sourceSectionForField = sourceGroupForField?.sections.find(
            (s: SectionWithId) =>
              s.fields.some((f: FieldWithId) => f.id === itemId),
          );
          const field = sourceSectionForField?.fields.find(
            (f: FieldWithId) => f.id === itemId,
          );
          if (field) {
            if (copyMode === 'cut') {
              sourceSectionForField.fields =
                sourceSectionForField.fields.filter(
                  (f: FieldWithId) => f.id !== itemId,
                );
            }
            const fieldToAdd =
              copyMode === 'copy'
                ? { ...field, id: crypto.randomUUID() }
                : field;
            const destGroupForField = updatedTemplates[
              destinationPropertyType
            ].groups.find((g: GroupWithId) => g.id === selectedGroup?.id);
            const destSectionForField = destGroupForField?.sections.find(
              (s: SectionWithId) => s.id === selectedSection.id,
            );
            if (destSectionForField) {
              destSectionForField.fields.push(fieldToAdd);
            }
          }
          break;
        }
      }
    });

    // Update the entire local template
    setLocalTemplate(updatedTemplates);

    // Update selected group and section if they're still relevant
    if (selectedGroup) {
      const updatedGroup = updatedTemplates[
        destinationPropertyType
      ].groups.find((g: GroupWithId) => g.id === selectedGroup.id);
      setSelectedGroup(updatedGroup || null);

      if (selectedSection && updatedGroup) {
        const updatedSection = updatedGroup.sections.find(
          (s: SectionWithId) => s.id === selectedSection.id,
        );
        setSelectedSection(updatedSection || null);
      } else {
        setSelectedSection(null);
      }
    }

    // Reset copy mode and clipboard after pasting
    setCopyMode(null);
    setClipboard(null);
    setSelectedItems([]);
  }, [
    clipboard,
    copyMode,
    selectedItem,
    selectedGroup,
    selectedSection,
    localTemplate,
    templateKey,
  ]);

  useHotkeys('ctrl+v', handlePaste, [handlePaste]);

  const renderMainView = () => {
    if (!selectedItem) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Typography variant="h6" gutterBottom>
            Select a module from the left to begin editing
          </Typography>
        </Box>
      );
    }

    if (translationFields.includes(selectedItem)) {
      return <LocaleEditor translationType={selectedItem} />;
    }

    if (lookupsFields.includes(selectedItem)) {
      return <RoomFeaturesEditor />;
    }

    if (isHelpTextResource && selectedHelpTextResource) {
      return (
        <HelpTextEditor
          resourceType={selectedHelpTextResource}
          helpText={helpText}
          onHelpTextChange={setHelpText}
        />
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" onClick={() => handleItemClick('')}>
              Templates
            </Link>
            {selectedItem && (
              <Link
                color="inherit"
                onClick={() => handleItemClick(selectedItem)}
              >
                {selectedItem}
              </Link>
            )}
            {selectedGroup && editMode === 'fields' && (
              <Link
                color="inherit"
                onClick={() => handleGroupClick(selectedGroup)}
              >
                {selectedGroup.name}
              </Link>
            )}
            {selectedSection && editMode === 'fields' && (
              <Typography color="text.primary">
                {selectedSection.name}
              </Typography>
            )}
          </Breadcrumbs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditModeToggle
              editMode={editMode}
              onEditModeChange={setEditMode}
            />

            {copyMode && (
              <IconButton
                color={copyMode === 'cut' ? 'error' : 'warning'}
                size="small"
              >
                {copyMode === 'cut' ? <ContentCutIcon /> : <ContentCopyIcon />}
              </IconButton>
            )}

            <SelectionModeToggle
              isSelectionMode={isSelectionMode}
              onToggle={handleSelectionModeToggle}
              selectionType={selectionType}
              selectionCount={selectedItems.length}
            />
          </Box>
        </Box>

        {editMode === 'fields' && (
          <TemplateFieldEditor
            template={
              localTemplate[templateKey as keyof PropertyTemplatesWithIds]
            }
            selectedGroup={selectedGroup}
            selectedSection={selectedSection}
            onGroupClick={handleGroupClick}
            onSectionClick={handleSectionClick}
            onUpdateGroups={updateGroups}
            onUpdateSections={updateSections}
            onUpdateFields={updateFields}
            setSelectedGroup={setSelectedGroup}
            setSelectedSection={setSelectedSection}
            selectedPropertyType={templateKey}
            localTemplate={localTemplate}
            setLocalTemplate={setLocalTemplate}
            isSelectionMode={isSelectionMode}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
          />
        )}

        {editMode === 'groupings' && (
          <TemplateGroupingsEditor
            template={
              localTemplate[templateKey as keyof PropertyTemplatesWithIds]
            }
            onUpdateGroupings={updateGroupings}
            propertyTemplates={localTemplate}
            currentPropertyType={templateKey}
            isSelectionMode={isSelectionMode}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            md: 2,
          }}
        >
          <List
            subheader={
              <ListSubheader component="div">
                Template <i>v{currentVersion}</i>
              </ListSubheader>
            }
            sx={{
              '& .Mui-selected': {
                borderLeft: '4px solid #1976d2',
                bgcolor: 'rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            <ListItemButton onClick={handleTemplatesToggle}>
              <ListItemText primary="Templates" />
              {templatesOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={templatesOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {propertyOptions.map((option) => (
                  <ListItemButton
                    key={option.LookupKey}
                    onClick={() => handleItemClick(option.LookupValue)}
                    selected={selectedItem === option.LookupValue}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText
                      primary={option.DisplayValue || option.LookupValue}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            <ListItemButton onClick={handleTranslationToggle}>
              <ListItemText primary="Translation" />
              {translationOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={translationOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {translationFields.map((field) => (
                  <ListItemButton
                    key={field}
                    onClick={() => handleItemClick(field)}
                    selected={selectedItem === field}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText primary={field} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            <ListItemButton onClick={handleLookupsToggle}>
              <ListItemText primary="Lookups" />
              {lookupsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={lookupsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {lookupsFields.map((field) => (
                  <ListItemButton
                    key={field}
                    onClick={() => handleItemClick(field)}
                    selected={selectedItem === field}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText primary={field} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            <ListItemButton onClick={handleHelpTextToggle}>
              <ListItemText primary="Help Text" />
              {helpTextOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={helpTextOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {helpTextResourceTypes.map((resourceType) => (
                  <ListItemButton
                    key={resourceType}
                    onClick={() => handleItemClick(`HelpText:${resourceType}`)}
                    selected={selectedItem === `HelpText:${resourceType}`}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText primary={resourceType} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </List>
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveTemplate}
              fullWidth
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 10,
          }}
        >
          {renderMainView()}
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showToast}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        message={toastMessage}
      />
    </Box>
  );
};

export default TemplateEditor;
